import React, { useState, useRef, useEffect, useId } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, Sparkles, MessageCircle, X, Check } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useCockpit } from "./cockpitContext";
import { SECTION_GUIDE } from "./guideContent";

const PAGE_HELP = {
  overview: "Ihre wichtigsten Informationen auf einen Blick.",
  website: "Status, Sicherheit, Backup und Performance Ihrer Website.",
  leads: "Alle Anfragen über Ihre Website an einem Ort.",
  tasks: "Alles, was Sie prüfen, freigeben oder mit KEWAG abstimmen müssen.",
  freigaben: "Entwürfe und Vorschläge von KEWAG, die auf Ihre Freigabe warten.",
  support: "Fragen, Probleme und Antworten direkt mit KEWAG.",
  performance: "Ihre monatlichen Ergebnisse einfach erklärt.",
  invoices: "Ihre Rechnungen, Verträge, Dokumente, Paketinformationen und Laufzeit an einem Ort.",
  documents: "Dateien, Fotos oder Wünsche an KEWAG senden und deren Status verfolgen.",
  "next-steps": "Empfehlungen und nächste Schritte für Ihren Auftritt.",
  reports: "Ihre monatlichen Ergebnisse einfach erklärt.",
  design: "Vorschläge von KEWAG zur Verbesserung Ihrer Website ansehen und freigeben.",
  reviews: "Ihren Bewertungslink nutzen und erhaltene Bewertungen im Blick behalten.",
  requests: "Ihre bisher eingereichten Wünsche und deren Bearbeitungsstand.",
  team: "Mitarbeiter einladen und Zugriffe steuern.",
  security: "Passwort, 2FA und Sicherheitseinstellungen.",
};

const PAGE_TITLE = {
  overview: "Überblick", website: "Website", leads: "Anfragen", tasks: "Aufgaben & Freigaben",
  freigaben: "Entwürfe freigeben", support: "Support", performance: "Besuche & Performance",
  invoices: "Rechnungen", documents: "Dateien & Freigaben", "next-steps": "Nächste Schritte",
  reports: "Monatsreport", design: "Website verbessern", reviews: "Bewertungen",
  requests: "Meine Wünsche", team: "Team & Benutzer", security: "Konto & Sicherheit",
};

export { PAGE_HELP, PAGE_TITLE };

export default function CockpitHelp({ variant = "desktop" }) {
  const { t } = useI18n();
  const { accent = "#A3E635", startTour, openAi, clientId } = useCockpit() || {};
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const helpId = useId();
  const loc = useLocation();

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false); triggerRef.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const seg = loc.pathname.split("/").filter(Boolean).pop();
  const key = (!seg || seg === "cockpit" || seg === clientId) ? "overview" : seg;
  const helpText = PAGE_HELP[key] || PAGE_HELP.overview;
  const title = PAGE_TITLE[key] || PAGE_TITLE.overview;
  const todo = SECTION_GUIDE[key] || [];

  return (
    <div ref={ref} className="relative">
      <button ref={triggerRef} onClick={() => setOpen((o) => !o)} data-testid={`cockpit-help-btn-${variant}`} aria-label={t("Hilfe & Rundgang")} aria-expanded={open} aria-controls={helpId}
        className="w-11 h-11 rounded-full flex items-center justify-center text-gray-300 hover:text-white hover:bg-white/10 transition-colors border border-white/10">
        <HelpCircle className="w-5 h-5" />
      </button>
      {open && (
        <div id={helpId} role="region" aria-label={t("Hilfe zu dieser Seite")} className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] rounded-2xl bg-[#15151b] border border-white/10 shadow-2xl overflow-hidden z-[9000]" data-testid={`cockpit-help-menu-${variant}`}>
          <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between gap-2">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }}>{t("Hilfe zu dieser Seite")}</div>
            <button onClick={() => { setOpen(false); triggerRef.current?.focus(); }} aria-label={t("Schliessen")} className="min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <div className="px-4 py-3.5 max-h-[70vh] overflow-y-auto">
            <div className="text-sm font-bold text-white">{t(title)}</div>
            <div className="mt-2.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-500">{t("Das sehen Sie hier")}</div>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed" data-testid={`cockpit-help-text-${variant}`}>{t(helpText)}</p>
            {todo.length > 0 && (
              <>
                <div className="mt-3 text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: accent }}>{t("Das tun Sie hier")}</div>
                <ul className="mt-1.5 space-y-1.5" data-testid={`cockpit-help-todo-${variant}`}>
                  {todo.map((b, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs text-gray-300">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent }} /><span>{t(b)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
          <div className="px-3 pb-3 space-y-2">
            <button onClick={() => { setOpen(false); startTour?.(); }} data-testid={`cockpit-help-tour-${variant}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-black font-bold text-sm transition-transform active:scale-[0.98] hover:brightness-105" style={{ background: accent }}>
              <Sparkles className="w-4 h-4" /> {t("Cockpit erklären")}
            </button>
            <button onClick={() => { setOpen(false); openAi?.(); }} data-testid={`cockpit-help-ask-${variant}`}
              className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white font-semibold text-sm hover:bg-white/10 transition-colors">
              <MessageCircle className="w-4 h-4" /> {t("Frage stellen")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
